import {
  toKvRows,
  mapPostmanAuth,
  mapPostmanBody,
  mapPostmanResponses,
  extractPostmanUrl,
  normalizeMethod,
  variablesToCollectionSettings,
} from './normalize';
import { defaultCollectionAuth } from '../requestAuth';
import { generateFolderId } from '../collectionFolders';

/**
 * Extract pre-request + test scripts from a Postman `event[]` array.
 * `script.exec` is an array of source lines (joined) or, rarely, a raw string.
 * Returns { preRequestScript?, testScript? } with only non-empty scripts.
 */
function extractPostmanScripts(events) {
  const out = {};
  for (const ev of Array.isArray(events) ? events : []) {
    if (!ev || !ev.script) continue;
    const exec = ev.script.exec;
    const text = Array.isArray(exec) ? exec.join('\n') : (typeof exec === 'string' ? exec : '');
    if (!text.trim()) continue;
    if (ev.listen === 'prerequest') out.preRequestScript = text;
    else if (ev.listen === 'test') out.testScript = text;
  }
  return out;
}

/** Spread scripts + Postman dialect tag onto a normalized node when any exist. */
function withScripts(node, scripts) {
  if (scripts.preRequestScript) node.preRequestScript = scripts.preRequestScript;
  if (scripts.testScript) node.testScript = scripts.testScript;
  if (scripts.preRequestScript || scripts.testScript) node.scriptDialect = 'pm';
  return node;
}

function mapPostmanRequest(item, folderId, sortOrder, warnings) {
  const req = item.request;
  if (!req) return null;

  const { url, params: urlParams } = extractPostmanUrl(req.url);
  const headerRows = toKvRows(req.header || []);
  const queryFromReq = toKvRows(req.url?.query || []);
  const params = queryFromReq.length ? queryFromReq : urlParams;
  const bodyMapped = mapPostmanBody(req.body, warnings);
  const { body, body_type, form_data_fields } = bodyMapped;
  const auth = mapPostmanAuth(req.auth || { type: 'inherit' }, warnings, 'request');
  // form_data_fields carries the enabled/disabled + type/file rows for both
  // multipart (formdata) and urlencoded (form) bodies; the runner reads them.
  const settings =
    (body_type === 'formdata' || body_type === 'form') && form_data_fields?.length
      ? { form_data_fields }
      : {};

  const mapped = {
    name: item.name || 'Request',
    description: item.description || req.description || '',
    method: normalizeMethod(req.method),
    url,
    headers: headerRows,
    params,
    cookies: [],
    body,
    body_type,
    auth,
    settings,
    folder_id: folderId,
    sort_order: sortOrder,
  };
  withScripts(mapped, extractPostmanScripts(item.event));
  const mock = mapPostmanResponses(item.response);
  if (mock) mapped.mock = mock;
  if (item.protocolProfileBehavior && typeof item.protocolProfileBehavior === 'object') {
    mapped.protocolProfileBehavior = item.protocolProfileBehavior;
  }
  return mapped;
}

/**
 * Walk Postman items; build folders[] and requests[] with folder_id.
 */
function walkPostmanItems(items, parentFolderId, folders, requests, warnings, orderRef) {
  if (!Array.isArray(items)) return;

  for (const item of items) {
    if (!item) continue;

    if (Array.isArray(item.item)) {
      const folderId = generateFolderId();
      const sortOrder = orderRef.value++;
      const folder = {
        id: folderId,
        name: item.name || 'Folder',
        parent_id: parentFolderId,
        sort_order: sortOrder,
      };
      withScripts(folder, extractPostmanScripts(item.event));
      if (item.auth) folder.auth = mapPostmanAuth(item.auth, warnings, 'folder');
      if (Array.isArray(item.variable) && item.variable.length) {
        // Same row shape as collection variables; a disabled Postman row stays
        // disabled instead of arriving switched on (utils/folderVariables).
        folder.variables = item.variable.map((v) => ({
          key: v.key,
          value: String(v.value ?? ''),
          enabled: v.disabled !== true,
          ...(v.type === 'secret' ? { secret: true } : {}),
        }));
      }
      folders.push(folder);
      walkPostmanItems(item.item, folderId, folders, requests, warnings, orderRef);
      continue;
    }

    const mapped = mapPostmanRequest(item, parentFolderId, orderRef.value++, warnings);
    if (mapped) requests.push(mapped);
  }
}

/**
 * @param {object} root Postman collection JSON
 * @param {string[]} warnings
 */
export function parsePostmanCollection(root, warnings) {
  const collection = root.collection && root.collection.item ? root.collection : root;
  const info = collection.info || {};
  const name = info.name || collection.name || 'Imported Collection';
  const description = collection.description || info.description || '';

  const folders = [];
  const requests = [];
  const orderRef = { value: 0 };

  walkPostmanItems(collection.item, null, folders, requests, warnings, orderRef);

  const settings = {
    ...variablesToCollectionSettings(collection.variable),
    folders,
  };

  if (collection.auth) {
    settings.collection_auth = mapPostmanAuth(collection.auth, warnings, 'collection');
  } else {
    settings.collection_auth = defaultCollectionAuth();
  }

  const normalized = {
    name,
    description: typeof description === 'string' ? description : '',
    settings,
    requests,
  };
  const collectionScripts = extractPostmanScripts(collection.event);
  withScripts(normalized, collectionScripts);
  // Also in `settings`: it is the only part of the collection object both import
  // paths persist, and where Run collection reads it (utils/scriptChain).
  withScripts(settings, collectionScripts);

  return [normalized];
}
